// extendTheme.js
const lightColorScheme = {
  // global typography settings – anything that uses MUI's
  // Typography component (or inherits from it) will pick up
  // this font stack automatically. you no longer need to add
  // `fontFamily` in every sx prop.
  typography: {
    fontFamily: "Inter, system-ui, sans-serif",
    h1: {
      color: "#ffffffff",
      fontFamily: "Syne, sans-serif",
    },
    h2: {
      color: "#ffffffff",
      fontFamily: "Space Grotesk, Inter, system-ui, sans-serif",
    },
    h3: {
      color: "#3784b4ff",
      fontFamily: "Space Grotesk, Inter, system-ui, sans-serif",
    },
    h4: {
      color: "#ffffffff",
    },
    h5: {
      color: "#000000ff",
    },
    h6: {
      color: "#000000ff",
    },
    body2: {
      color: "#ffffffff",
    },
    overline: {
      color: "#008cffff",
    },
    textXs: {
      fontSize: '0.75rem',
      lineHeight: '1rem',
    },
    textSm: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    },
    textLg: {
      fontSize: '1.125rem',
      lineHeight: '1.75rem',
    },
    textXl: {
      fontSize: '1.25rem',
      lineHeight: '1.75rem',
    },
    text2xl: {
      fontSize: '1.5rem',
      lineHeight: '2rem',
    },
    text3xl: {
      fontSize: '1.875rem',
      lineHeight: '2.25rem',
    },
    text4xl: {
      fontSize: '2.25rem',
      lineHeight: '2.5rem',
    }
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: "#FFF",
          boxShadow:
            "0px 2px 4px -1px #f2f2f2, 0px 4px 5px 0px #f2f2f2, 0px 1px 10px 0px #f2f2f2;",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 50,
          textTransform: "none",
          letterSpacing: 1.2,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 50,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow:
            "0px 2px 4px -1px #f2f2f2, 0px 4px 5px 0px #f2f2f2, 0px 1px 10px 0px #f2f2f2;",
        },
      },
    },
  },
  palette: {
    mode: "light",
    primary: {
      main: "#005aa3", // --tw-gradient-from
      light: "#47ade9",
      dark: "#004a80", // --tw-gradient-to
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#FCD34D",
      light: "#e4e641",
      dark: "#b45309",
    },
    success: {
      main: "hsl(172, 66%, 40%)",
      light: "hsl(172, 66%, 40% / 0.5)",
      dark: "hsl(172, 66%, 40% / 0.8)",
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
      section: "#f1f5f9",
      service: "hsl(210 15% 93% / 0.5)",
      footer: "hsl(213 30% 15%)",
      chip: "#e3f2fd",
      muted: "#f2f4f8",
      developer: "hsl(214 32% 96% / 0.3)",
    },
    text: {
      primary: "#0f172a",
      secondary: "#64748b",
      white: "#ffffff",
      link: "#005f8d",
      heroSecondary: "#c1e8f5",
      muted: "#65758b",
    },
    custom: {
      white: "#ffffffe6",

      // Gradients
      heroGradient: `radial-gradient(ellipse 100% 30% at 50% 100%, rgba(255, 255, 255, 0.45) 50%, transparent 100%),
                     linear-gradient(135deg, hsl(207 100% 32%) 0%, hsl(195 95% 43%) 50%, hsl(207 100% 25%) 100%)`,
      accentGradient:
        "linear-gradient(135deg, hsl(66, 100%, 91%) 0%, hsl(64, 100%, 73%) 50%, hsl(66, 94%, 87%) 100%)",
      buttonGradient:
        "linear-gradient(to right, hsl(207 100% 32%), hsl(195 95% 43%))",
      headingGradient: "linear-gradient(135deg, #005aa3, #05a2d6)",
      projectGradient: `linear-gradient(135deg, hsl(207 100% 32%) 0%, hsl(195 95% 43%) 50%, hsl(207 100% 25%) 100%)`,


      //Project State Shadow
      activeBlueShadow: "0 8px 20px rgba(0, 90, 163, 0.45)",

      // Hero Specifics
      heroBadgeBg: "#ffffff1a",
      heroBorder: "#fff3",
      glassBorder: "rgba(238, 238, 238, 0.4)",
      statCardBg: "rgba(255, 255, 255, 0.1)",
      statHover: "rgba(228, 228, 228, 0.2)",

      // Components
      chipText: "#005aa3",
      tagBg: "hsl(61 77% 58% / 0.2)",
      tagText: "hsl(213 30% 15%)",
      cardShadow: "0 4px 20px rgba(0,0,0,0.05)",
      cardShadowHover: "0 14px 30px rgba(0,0,0,0.12)",
      overlay: "rgba(0, 0, 0, 0.4)",

      // View All Projects Button
      viewAllBtnBorder: "#005aa3",
      viewAllBtnText: "#005aa3",
      viewAllBtnBg: "#ffffff",
      viewAllBtnHoverBg: "#005aa3",
      viewAllBtnHoverText: "#ffffff",

      // Mobile Menu
      mobileMenuBg: "linear-gradient(180deg, #e3f2fd 0%, #bbdefb 100%)",
      mobileMenuText: "#005aa3",
      mobileMenuBtnText: "#ffffff",

      // Services Section
      serviceIconBg:
        "linear-gradient(to right, hsl(207 100% 32%), hsl(195 95% 43%))",
      serviceIconColor: "#ffffff",

      // Navbar
      navbarBgScrolled: "rgba(255, 255, 255, 0.7)",
      navbarTextScrolled: "hsla(214, 30%, 15%, 0.80)",

      // Footer
      footerText: "hsl(210 20% 98% / 0.6)",
      footerTitle: "#ffffff",
      footerIconBg: "rgba(255, 255, 255, 0.1)",
      footerIconColor: "rgba(255, 255, 255, 0.85)",
      footerLinkHover: "#fbff00ff",
      footerDivider: "rgba(255, 255, 255, 0.12)",
      footerCopyright: "rgba(255, 255, 255, 0.55)",

      // Developer Section
      developerRoleColor: "#23a997",
      developerBorderColor: "hsl(222 47% 20% / 0.2)",
    },
    // Retaining legacy colors just in case to avoid crashes, mapped to new system where reasonable
    inactive_text: { main: "#BFD4E4" },
    notactive_text: { main: "#B5B7C0" },
    table_text: { main: "#444A6D" },
    table_border: { main: "#DDEAF3" },
    thead_bg: { main: "#F2F7F9" },
    boxBg: { main: "#F3F6F9" },
    boxBorder: { main: "#CCCCCC" },
    white: { main: "#FFF", light: "#FFF", dark: "#FFF" },
    info: { main: "#6699FF" },
    navy: { main: "#00B6D5" },
    danger: { main: "#d32f2f" },
  },
};

const darkColorScheme = {
  typography: {
    fontFamily: "Inter, system-ui, sans-serif",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
    h1: {
      color: "#FF6600",
      fontFamily: "Space Grotesk, Inter, system-ui, sans-serif",
    },
    h2: {
      fontSize: "1.5rem",
      fontFamily: "Space Grotesk, Inter, system-ui, sans-serif",
    },
  },
  palette: {
    mode: "dark",
    primary: {
      main: "#FF6600",
    },
    secondary: {
      main: "#000099",
    },
    textPrimary: {
      main: "#6699FF",
    },
    textSecondary: {
      main: "#00B6D5",
    },
  },
};

const modalBox = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  maxHeight: "80%",
  overflow: "auto",
  bgcolor: "background.paper",
  borderRadius: 5,
  outline: "none",
  boxShadow: 5,
  p: 4,
};

export { lightColorScheme, darkColorScheme, modalBox };
